import React, { useState, useEffect, useCallback } from 'react';
import { ContractRecipient, ContractRecipientCreate, Contract } from '../types';
import { apiService } from '../services/api';
import { authService } from '../services/authService';
import Modal from './Modal';
import ContractRecipientForm from './ContractRecipientForm';

interface ContractRecipientsProps {
  contract: Contract;
  onUpdate?: () => void;
}

const ContractRecipients: React.FC<ContractRecipientsProps> = ({ contract, onUpdate }) => {
  const [recipients, setRecipients] = useState<ContractRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState<ContractRecipient | null>(null);

  const fetchRecipients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getRecipientsByContract(contract.contract_id);
      setRecipients(response.recipients || []);
    } catch (error) {
      console.error('Error fetching contract recipients:', error);
    } finally {
      setLoading(false);
    }
  }, [contract.contract_id]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  const handleCreateRecipient = () => {
    setEditingRecipient(null);
    setShowModal(true);
  };

  const handleEditRecipient = (recipient: ContractRecipient) => {
    setEditingRecipient(recipient);
    setShowModal(true);
  };

  const handleDeleteRecipient = async (recipientId: string) => {
    if (window.confirm('Are you sure you want to delete this recipient?')) {
      try {
        const currentUser = authService.getUser();
        await apiService.deleteContractRecipient(recipientId, currentUser?.username || 'system');
        fetchRecipients();
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error('Error deleting contract recipient:', error);
        alert('Failed to delete recipient. Please try again.');
      }
    }
  };

  const handleToggleActive = async (recipient: ContractRecipient) => {
    try {
      const currentUser = authService.getUser();
      const username = currentUser?.username || 'system';
      
      if (recipient.is_active) {
        await apiService.deactivateContractRecipient(recipient.id, username);
      } else {
        await apiService.activateContractRecipient(recipient.id, username);
      }
      
      fetchRecipients();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error toggling recipient status:', error);
      alert('Failed to update recipient status. Please try again.');
    }
  };

  const handleSubmitRecipient = async (data: ContractRecipientCreate) => {
    try {
      if (editingRecipient) {
        const currentUser = authService.getUser();
        await apiService.updateContractRecipient(editingRecipient.id, {
          ...data,
          updated_by: currentUser?.username || 'system'
        });
      } else {
        await apiService.createContractRecipient(data);
      }
      
      setShowModal(false);
      fetchRecipients();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving contract recipient:', error);
      alert('Failed to save recipient. Please try again.');
    }
  };

  const getRecipientTypeBadge = (type: string) => {
    const typeClasses: { [key: string]: string } = {
      to: 'badge-primary',
      cc: 'badge-info',
      bcc: 'badge-secondary'
    };
    return typeClasses[type] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div className="spinner"></div>
        <p>Loading recipients...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3>Email Recipients</h3>
        <button className="btn btn-primary" onClick={handleCreateRecipient}>
          + Add Recipient
        </button>
      </div>

      {recipients.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h4 style={{ color: '#7f8c8d', marginBottom: '10px' }}>No Recipients</h4>
          <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
            This contract doesn't have any email recipients configured yet.
          </p>
          <button className="btn btn-primary" onClick={handleCreateRecipient}>
            Add First Recipient
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Email</th>
                <th>Name</th>
                <th>Contract Updates</th>
                <th>Invoice Communications</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((recipient) => (
                <tr key={recipient.id}>
                  <td>
                    <span className={`badge ${getRecipientTypeBadge(recipient.recipient_type)}`}>
                      {recipient.recipient_type.toUpperCase()}
                    </span>
                  </td>
                  <td>{recipient.email}</td>
                  <td>{recipient.name || '-'}</td>
                  <td>
                    <span className={`badge ${recipient.is_contract ? 'badge-success' : 'badge-secondary'}`}>
                      {recipient.is_contract ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${recipient.is_invoice ? 'badge-success' : 'badge-secondary'}`}>
                      {recipient.is_invoice ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${recipient.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {recipient.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(recipient.created_at).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleEditRecipient(recipient)}
                      style={{ marginRight: '5px' }}
                    >
                      Edit
                    </button>
                    <button
                      className={`btn btn-sm ${recipient.is_active ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleActive(recipient)}
                      style={{ marginRight: '5px' }}
                    >
                      {recipient.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteRecipient(recipient.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={editingRecipient ? 'Edit Recipient' : 'Add Recipient'}
          onClose={() => setShowModal(false)}
        >
          <ContractRecipientForm
            contractId={contract.contract_id}
            initialData={editingRecipient}
            onSubmit={handleSubmitRecipient}
            onCancel={() => setShowModal(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default ContractRecipients;